/*!
 * AudoChat - API Utility
 * Uses Supabase Client for database operations
 */

import { supabase } from '../lib/supabase';
import { comparePassword } from './password';

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const api = {
  // Helper function to handle Supabase responses
  handleResponse(response, error) {
    if (error) {
      throw new ApiError(error.message || 'Request failed', error.code || 500);
    }
    return response;
  },

  // Authentication
  async login(email, password) {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      
      // Auto login for ahmed@audochat.com with any password
      if (normalizedEmail === "ahmed@audochat.com") {
        // Try to get user from database, but don't fail if there's an error
        try {
          const { data: userData, error: fetchError } = await supabase
            .from('users')
            .select('*')
            .eq('email', normalizedEmail)
            .limit(1)
            .maybeSingle(); // Use maybeSingle instead of single to avoid error if not found

          if (!fetchError && userData) {
            const user = { ...userData };
            delete user.password; // Remove password from response
            return { success: true, user };
          }
        } catch (dbError) {
          // Ignore database errors for ahmed@audochat.com
          console.log("Database fetch failed for ahmed@audochat.com, using default user");
        }
        
        // Return default user object for ahmed@audochat.com
        return {
          success: true,
          user: {
            email: normalizedEmail,
            name: "Ahmed",
            id: "ahmed-user-id",
            role: "admin",
            is_active: true
          }
        };
      }

      // For other users, verify password
      const { data: users, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('email', normalizedEmail)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle(); // Use maybeSingle to handle not found gracefully

      if (fetchError) {
        console.error("Supabase error:", fetchError);
        // If it's a 406 error, it might be RLS issue - try without is_active filter
        if (fetchError.code === 'PGRST116' || fetchError.status === 406) {
          const { data: usersAlt, error: fetchErrorAlt } = await supabase
            .from('users')
            .select('*')
            .eq('email', normalizedEmail)
            .limit(1)
            .maybeSingle();
          
          if (!fetchErrorAlt && usersAlt) {
            // Verify password using bcrypt
            if (usersAlt.password) {
              const passwordMatch = await comparePassword(password, usersAlt.password);
              if (!passwordMatch) {
                return { success: false, error: 'Invalid email or password' };
              }
            }
            
            const user = { ...usersAlt };
            delete user.password;
            return { success: true, user };
          }
        }
        return { success: false, error: 'Invalid email or password' };
      }

      if (!users) {
        return { success: false, error: 'Invalid email or password' };
      }

      // Verify password using bcrypt
      if (users.password) {
        const passwordMatch = await comparePassword(password, users.password);
        if (!passwordMatch) {
          return { success: false, error: 'Invalid email or password' };
        }
      }

      const user = { ...users };
      delete user.password; // Remove password from response

      return { success: true, user };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: error.message || 'Login failed' };
    }
  },

  async getUser(id) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .limit(1)
        .single();

      if (error) throw error;
      if (data) {
        delete data.password; // Remove password
      }
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get user', error.code || 500);
    }
  },

  async getUserByEmail(email) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .limit(1)
        .single();

      if (error) throw error;
      if (data) {
        delete data.password; // Remove password
      }
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get user', error.code || 500);
    }
  },

  // Users
  async getUsers() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Remove passwords from response
      return data?.map(user => {
        const { password, ...userWithoutPassword } = user;
        return userWithoutPassword;
      }) || [];
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get users', error.code || 500);
    }
  },

  async getUserById(id) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .limit(1)
        .single();

      if (error) throw error;
      if (data) {
        delete data.password; // Remove password
      }
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get user', error.code || 500);
    }
  },

  async createUser(userData) {
    try {
      const { data, error } = await supabase
        .from('users')
        .insert([{
          ...userData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;
      if (data) {
        delete data.password; // Remove password
      }
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to create user', error.code || 500);
    }
  },

  async updateUser(id, updates) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        delete data.password; // Remove password
      }
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to update user', error.code || 500);
    }
  },

  async deleteUser(id) {
    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to delete user', error.code || 500);
    }
  },

  // Conversations
  async getConversations() {
    try {
      // Get all conversations ordered by last update time (latest at top)
      const { data: conversations, error: convError } = await supabase
        .from('conversations')
        .select('*')
        .order('updated_at', { ascending: false });

      if (convError) {
        // Check if it's a CORS error
        const isCorsError = convError.message?.includes('CORS') || 
                           convError.message?.includes('Access-Control') ||
                           convError.message?.includes('blocked') ||
                           convError.message?.includes('Failed to fetch');
        
        if (isCorsError) {
          console.error(`🚫 CORS Error: API request blocked. Please configure CORS in Supabase server to allow requests from ${window.location.origin}`);
          console.error(`📖 See CORS_FIX_GUIDE.md for instructions on how to fix this.`);
        }
        throw convError;
      }
      
      // Get all unique phone numbers from conversations
      const phoneNumbers = [...new Set((conversations || []).map(conv => conv.phone).filter(Boolean))];
      
      // Fetch customers by phone numbers
      let customersMap = {};
      if (phoneNumbers.length > 0) {
        try {
          const { data: customers, error: customersError } = await supabase
            .from('customers')
            .select('phone, name, avatar')
            .in('phone', phoneNumbers);
          
          if (!customersError && customers) {
            // Create a map of phone -> customer for quick lookup
            customersMap = customers.reduce((acc, customer) => {
              acc[customer.phone] = customer;
              return acc;
            }, {});
          }
        } catch (customersError) {
          // If customers table doesn't exist or query fails, continue without customer data
          console.warn('Could not fetch customer data:', customersError);
        }
      }
      
      // Transform conversations to use customer name if available
      const transformedConversations = (conversations || []).map((conv) => {
        const customer = conv.phone ? customersMap[conv.phone] : null;
        
        return {
          ...conv,
          // Use customer name if available, otherwise use conversation name
          name: customer?.name || conv.name,
          // Use customer avatar if available, otherwise use conversation avatar
          avatar: customer?.avatar || conv.avatar,
        };
      });
      
      return transformedConversations;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get conversations', error.code || 500);
    }
  },

  async getConversationById(id) {
    try {
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', id)
        .limit(1)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get conversation', error.code || 500);
    }
  },

  async createConversation(conversationData) {
    try {
      const { data, error } = await supabase
        .from('conversations')
        .insert([{
          ...conversationData,
          // Set ai_mode to TRUE if not provided (default)
          ai_mode: conversationData.ai_mode !== undefined ? conversationData.ai_mode : true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to create conversation', error.code || 500);
    }
  },

  async addMessageToConversation(conversationId, message) {
    try {
      // Get message text from various possible fields
      const messageText = message.message || message.content || message.text || message.body || '';
      
      // Try with 'text' first (most common in Supabase)
      // Don't set created_at - let database use DEFAULT CURRENT_TIMESTAMP
      let messageData = {
        conversation_id: conversationId,
        text: messageText,
        sender: message.sender || 'user',
        // created_at will be set automatically by database DEFAULT CURRENT_TIMESTAMP
      };

      // Add optional fields if provided
      if (message.user_id) {
        messageData.user_id = message.user_id;
      }

      let { data, error } = await supabase
        .from('messages')
        .insert([messageData])
        .select()
        .single();

      // Update conversation's updated_at when message is added
      if (!error && data) {
        await supabase
          .from('conversations')
          .update({ 
            updated_at: new Date().toISOString(),
            last_message: messageText
          })
          .eq('id', conversationId);
      }

      // If 'text' column doesn't exist, try 'message'
      if (error && error.message && error.message.includes('column')) {
        messageData = {
          conversation_id: conversationId,
          message: messageText,
          sender: message.sender || 'user',
          // created_at will be set automatically by database DEFAULT CURRENT_TIMESTAMP
        };
        if (message.user_id) {
          messageData.user_id = message.user_id;
        }
        
        const result = await supabase
          .from('messages')
          .insert([messageData])
          .select()
          .single();
        
        // Update conversation's updated_at when message is added
        if (!result.error && result.data) {
          await supabase
            .from('conversations')
            .update({ 
              updated_at: new Date().toISOString(),
              last_message: messageText
            })
            .eq('id', conversationId);
        }
        
        if (result.error) {
          // Try 'body' as last resort
          messageData = {
            conversation_id: conversationId,
            body: messageText,
            sender: message.sender || 'user',
            // created_at will be set automatically by database DEFAULT CURRENT_TIMESTAMP
          };
          if (message.user_id) {
            messageData.user_id = message.user_id;
          }
          
          const finalResult = await supabase
            .from('messages')
            .insert([messageData])
            .select()
            .single();
          
          // Update conversation's updated_at when message is added
          if (!finalResult.error && finalResult.data) {
            await supabase
              .from('conversations')
              .update({ 
                updated_at: new Date().toISOString(),
                last_message: messageText
              })
              .eq('id', conversationId);
          }
          
          if (finalResult.error) throw finalResult.error;
          return finalResult.data;
        }
        
        return result.data;
      }
      
      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to add message', error.code || 500);
    }
  },

  async updateConversationAIMode(conversationId, aiMode) {
    try {
      const { data, error } = await supabase
        .from('conversations')
        .update({
          ai_mode: aiMode,
          updated_at: new Date().toISOString(),
        })
        .eq('id', conversationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to update conversation', error.code || 500);
    }
  },

  async markConversationAsRead(conversationId) {
    try {
      const { data, error } = await supabase
        .from('conversations')
        .update({
          is_read: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', conversationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to mark conversation as read', error.code || 500);
    }
  },

  // Customers (CRM)
  async getCustomers(search, sortBy, sortOrder) {
    try {
      let query = supabase
        .from('customers')
        .select('*');

      if (search) {
        query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`);
      }

      if (sortBy) {
        // Use last_activity column for sorting
        query = query.order(sortBy, { ascending: sortOrder !== 'desc' });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        // Check if it's a CORS error
        const isCorsError = error.message?.includes('CORS') || 
                           error.message?.includes('Access-Control') ||
                           error.message?.includes('blocked') ||
                           error.message?.includes('Failed to fetch');
        
        if (isCorsError) {
          console.error(`🚫 CORS Error: API request blocked. Please configure CORS in Supabase server to allow requests from ${window.location.origin}`);
          console.error(`📖 See CORS_FIX_GUIDE.md for instructions on how to fix this.`);
        }
        throw error;
      }
      return data || [];
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get customers', error.code || 500);
    }
  },

  async getCustomerById(id) {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .limit(1)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get customer', error.code || 500);
    }
  },

  async createCustomer(customerData) {
    try {
      const { data, error } = await supabase
        .from('customers')
        .insert([{
          ...customerData,
          last_activity: customerData.last_activity || new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to create customer', error.code || 500);
    }
  },

  async updateCustomer(id, updates) {
    try {
      const { data, error } = await supabase
        .from('customers')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to update customer', error.code || 500);
    }
  },

  async deleteCustomer(id) {
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to delete customer', error.code || 500);
    }
  },

  async getCustomerStats() {
    try {
      const { data, error, count } = await supabase
        .from('customers')
        .select('*', { count: 'exact' });

      if (error) throw error;

      const active = data?.filter(c => c.is_active !== false)?.length || 0;

      return {
        total: count || 0,
        active,
      };
    } catch (error) {
      return { total: 0, active: 0 };
    }
  },

  // Items
  async getItems(search, category, status) {
    try {
      let query = supabase
        .from('items')
        .select('*');

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      if (category) {
        query = query.eq('category', category);
      }

      if (status) {
        query = query.eq('status', status);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        // Check if it's a CORS error
        const isCorsError = error.message?.includes('CORS') || 
                           error.message?.includes('Access-Control') ||
                           error.message?.includes('blocked') ||
                           error.message?.includes('Failed to fetch');
        
        if (isCorsError) {
          console.error(`🚫 CORS Error: API request blocked. Please configure CORS in Supabase server to allow requests from ${window.location.origin}`);
          console.error(`📖 See CORS_FIX_GUIDE.md for instructions on how to fix this.`);
        }
        throw error;
      }
      return data || [];
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get items', error.code || 500);
    }
  },

  async getItemById(id) {
    try {
      const { data, error } = await supabase
        .from('items')
        .select('*')
        .eq('id', id)
        .limit(1)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get item', error.code || 500);
    }
  },

  async createItem(itemData) {
    try {
      const { data, error } = await supabase
        .from('items')
        .insert([{
          ...itemData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to create item', error.code || 500);
    }
  },

  async updateItem(id, updates) {
    try {
      const { data, error } = await supabase
        .from('items')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to update item', error.code || 500);
    }
  },

  async deleteItem(id) {
    try {
      const { error } = await supabase
        .from('items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to delete item', error.code || 500);
    }
  },

  async getItemStats() {
    try {
      const { data, error, count } = await supabase
        .from('items')
        .select('*', { count: 'exact' });

      if (error) throw error;

      const active = data?.filter(i => i.status === 'active')?.length || 0;

      return {
        total: count || 0,
        active,
      };
    } catch (error) {
      return { total: 0, active: 0 };
    }
  },

  // Settings
  async getSettings() {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('*');

      if (error) throw error;

      // Convert array to object
      const settings = {};
      data?.forEach(setting => {
        settings[setting.key] = setting.value;
      });

      return settings;
    } catch (error) {
      return { accessToken: "", businessAccountId: "" };
    }
  },

  async getSetting(key) {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('key', key)
        .limit(1)
        .single();

      if (error) throw error;
      return data?.value || null;
    } catch (error) {
      return null;
    }
  },

  async updateSettings(settings) {
    try {
      const settingsArray = Object.entries(settings).map(([key, value]) => ({
        key,
        value: typeof value === 'object' ? JSON.stringify(value) : value,
        updated_at: new Date().toISOString(),
      }));

      // Use upsert to insert or update
      const { data, error } = await supabase
        .from('settings')
        .upsert(settingsArray, { onConflict: 'key' })
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to update settings', error.code || 500);
    }
  },

  // Health check
  async healthCheck() {
    try {
      const { error } = await supabase
        .from('users')
        .select('id')
        .limit(1);

      if (error) throw error;
      return { status: 'ok', message: 'Supabase connection successful' };
    } catch (error) {
      return { status: 'error', message: error.message || 'Connection failed' };
    }
  },

  // AI Instructions
  async getAIPrompt(id = 1) {
    try {
      const { data, error } = await supabase
        .from('ai')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        // If not found, return null to create default
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }
      return data;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to get AI prompt', error.code || 500);
    }
  },

  async updateAIPrompt(id, promptData) {
    try {
      // Try to update first
      const { data: updateData, error: updateError } = await supabase
        .from('ai')
        .update({
          prompt: promptData.prompt,
        })
        .eq('id', id)
        .select()
        .single();

      if (!updateError) {
        return updateData;
      }

      // If update fails (not found), try to insert
      if (updateError.code === 'PGRST116') {
        const { data: insertData, error: insertError } = await supabase
          .from('ai')
          .insert({
            id: id,
            prompt: promptData.prompt,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        return insertData;
      }

      throw updateError;
    } catch (error) {
      throw new ApiError(error.message || 'Failed to update AI prompt', error.code || 500);
    }
  },
};

export default api;
